import React from 'react';
import { hotelInfo } from '../mockData';
import { useSEO } from '../hooks/useSEO';

const TermsOfServicePage = () => {
  useSEO({
    title: 'Terms of Service | OlivOak Villa',
    description: 'Read the terms and conditions for booking and staying at OlivOak Villa, Harare. Cancellation policy, guest conduct, property rules, and more.'
  });

  return (
    <main className="min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
      {/* Hero */}
      <section
        className="py-20"
        style={{ backgroundColor: 'var(--bg-secondary)' }}
      >
        <div className="container mx-auto px-6 md:px-10 text-center">
          <h1 className="heading-1 mb-4">Terms of Service</h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
            Please read these terms carefully before using our services
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding">
        <div className="container mx-auto px-6 md:px-10" style={{ maxWidth: '800px' }}>
          <div className="space-y-10">
            <div>
              <p className="body-small mb-2" style={{ color: 'var(--text-light)' }}>
                Last updated: March 2026
              </p>
              <p className="body-base" style={{ color: 'var(--text-secondary)' }}>
                Welcome to {hotelInfo.name}. By accessing our website or making a reservation, you agree
                to be bound by these Terms of Service. If you do not agree with any part of these terms,
                please do not use our services.
              </p>
            </div>

            <div>
              <h2 className="heading-3 mb-3">1. Reservations and Bookings</h2>
              <ul className="list-disc pl-6 space-y-2 body-base" style={{ color: 'var(--text-secondary)' }}>
                <li>All reservations are subject to availability and confirmation by {hotelInfo.name}.</li>
                <li>A valid form of payment is required to secure your booking.</li>
                <li>Room rates are quoted per night and may vary based on season, room type, and occupancy.</li>
                <li>Special requests (e.g. early check-in, extra bedding) are accommodated where possible but cannot be guaranteed.</li>
                <li>Guests must be at least 18 years of age to make a reservation.</li>
              </ul>
            </div>

            <div>
              <h2 className="heading-3 mb-3">2. Check-In and Check-Out</h2>
              <ul className="list-disc pl-6 space-y-2 body-base" style={{ color: 'var(--text-secondary)' }}>
                <li>Check-in time is from 14:00 and check-out time is by 10:00 unless otherwise arranged.</li>
                <li>Early check-in and late check-out are subject to availability and may incur additional charges.</li>
                <li>A valid government-issued photo ID is required at check-in for all guests.</li>
              </ul>
            </div>

            <div>
              <h2 className="heading-3 mb-3">3. Cancellation and Refund Policy</h2>
              <p className="body-base mb-3" style={{ color: 'var(--text-secondary)' }}>
                All reservations are non-refundable unless otherwise stated. By completing your booking,
                you acknowledge and accept the following cancellation terms:
              </p>
              <ul className="list-disc pl-6 space-y-2 body-base" style={{ color: 'var(--text-secondary)' }}>
                <li>Cancellations made more than 48 hours before the check-in date will receive a full refund.</li>
                <li>For reservations made more than 28 days prior to the check-in date and for stays of seven nights or longer, cancellations must be submitted no later than seven days before the scheduled check-in date to qualify for a full refund.</li>
                <li>Cancellations made within 48 hours of check-in will be charged the equivalent of one night's stay.</li>
                <li>No-shows will be charged the full amount of the reservation.</li>
                <li>{hotelInfo.name} reserves the right to modify cancellation terms for peak seasons and special events, which will be communicated at the time of booking.</li>
              </ul>
              <p className="body-base mt-3 font-medium" style={{ color: 'var(--text-primary)' }}>
                Please ensure you understand the exact charges that apply before confirming your reservation to avoid any disputes.
              </p>
            </div>

            <div>
              <h2 className="heading-3 mb-3">4. Payment Terms</h2>
              <p className="body-base" style={{ color: 'var(--text-secondary)' }}>
                We accept payments through our secure online payment gateway. Full payment or a deposit
                may be required at the time of booking, depending on the rate plan selected. All prices
                are quoted in the displayed currency and include applicable taxes unless otherwise stated.
              </p>
            </div>

            <div>
              <h2 className="heading-3 mb-3">5. Guest Conduct</h2>
              <p className="body-base mb-3" style={{ color: 'var(--text-secondary)' }}>
                Guests are expected to conduct themselves in a respectful and responsible manner at all times during their stay.
                {' '}{hotelInfo.name} reserves the right to refuse service, terminate a stay, or request the immediate departure of any guests who:
              </p>
              <ul className="list-disc pl-6 space-y-2 body-base" style={{ color: 'var(--text-secondary)' }}>
                <li>Engage in disruptive, abusive, or unlawful behaviour</li>
                <li>Cause damage to Villa property</li>
                <li>Violate the safety, comfort, or privacy of other guests or staff</li>
                <li>Exceed the maximum occupancy permitted for the reserved accommodation</li>
                <li>Engage in public indecency</li>
                <li>Conduct commercial solicitation or business activities on the premises without prior authorisation</li>
              </ul>
              <h3 className="heading-3 mt-6 mb-3" style={{ fontSize: '1rem' }}>Additional Rules</h3>
              <ul className="list-disc pl-6 space-y-2 body-base" style={{ color: 'var(--text-secondary)' }}>
                <li>Quiet hours are observed between 22:00 and 06:00.</li>
                <li>Smoking is strictly prohibited in the rooms or indoor areas of the property. A designated outdoor smoking area is available.</li>
                <li>The following are strictly prohibited on the premises: pets (except registered service animals), weapons, illegal substances, and fireworks.</li>
              </ul>
            </div>

            <div>
              <h2 className="heading-3 mb-3">6. Property Damage</h2>
              <p className="body-base" style={{ color: 'var(--text-secondary)' }}>
                Guests are responsible for any damage to the Villa caused by themselves or their accompanying
                guests during their stay. Guests are also liable for any loss or removal of items from the
                property without the owner's consent. {hotelInfo.name} reserves the right to charge the
                guest's payment method for the cost of repairing or replacing any damaged, lost, or missing items.
              </p>
            </div>

            <div>
              <h2 className="heading-3 mb-3">7. Policies and Procedures</h2>
              <ul className="list-disc pl-6 space-y-2 body-base" style={{ color: 'var(--text-secondary)' }}>
                <li>Minors are not permitted to stay at {hotelInfo.name} without the supervision or consent of a parent or legal guardian.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Entry to Rooms:</strong> {hotelInfo.name} staff reserve the right to enter guest rooms for cleaning, maintenance, safety inspections, or other necessary purposes. In emergency situations, staff may enter a room even if a "Do Not Disturb" sign is displayed.</li>
              </ul>
            </div>

            <div>
              <h2 className="heading-3 mb-3">8. Liability</h2>
              <p className="body-base" style={{ color: 'var(--text-secondary)' }}>
                {hotelInfo.name} shall not be held liable for any loss, theft, or damage to guests'
                personal belongings during their stay. Guests are advised to use in-room safes where
                available and to take reasonable precautions with their valuables. We are not liable for
                any indirect, incidental, or consequential damages arising from the use of our website
                or services.
              </p>
            </div>

            <div>
              <h2 className="heading-3 mb-3">9. Intellectual Property</h2>
              <p className="body-base" style={{ color: 'var(--text-secondary)' }}>
                All content on this website — including text, images, logos, graphics, and design — is the
                property of {hotelInfo.name} and is protected by copyright and intellectual property laws.
                You may not reproduce, distribute, or use any content without our prior written consent.
              </p>
            </div>

            <div>
              <h2 className="heading-3 mb-3">10. Website Use</h2>
              <ul className="list-disc pl-6 space-y-2 body-base" style={{ color: 'var(--text-secondary)' }}>
                <li>You agree to use our website only for lawful purposes and in accordance with these terms.</li>
                <li>You must not attempt to gain unauthorised access to our systems or interfere with the website's functionality.</li>
                <li>We reserve the right to modify, suspend, or discontinue any part of the website at any time without notice.</li>
              </ul>
            </div>

            <div>
              <h2 className="heading-3 mb-3">11. Force Majeure</h2>
              <p className="body-base" style={{ color: 'var(--text-secondary)' }}>
                {hotelInfo.name} shall not be liable for any failure to perform obligations due to
                circumstances beyond our reasonable control, including but not limited to natural disasters,
                pandemics, government actions, civil unrest, power failures, or other force majeure events.
              </p>
            </div>

            <div>
              <h2 className="heading-3 mb-3">12. Changes to These Terms</h2>
              <p className="body-base" style={{ color: 'var(--text-secondary)' }}>
                We reserve the right to update these Terms of Service at any time. Changes will be posted
                on this page with a revised "Last updated" date. Continued use of our website or services
                after any changes constitutes acceptance of the updated terms.
              </p>
            </div>

            <div>
              <h2 className="heading-3 mb-3">13. Governing Law</h2>
              <p className="body-base" style={{ color: 'var(--text-secondary)' }}>
                These terms shall be governed by and construed in accordance with the laws of Zimbabwe.
                Any disputes arising from these terms or your use of our services shall be subject to
                the exclusive jurisdiction of the courts of Zimbabwe.
              </p>
            </div>

            <div>
              <h2 className="heading-3 mb-3">14. Contact Us</h2>
              <p className="body-base" style={{ color: 'var(--text-secondary)' }}>
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="mt-3 space-y-1 body-base" style={{ color: 'var(--text-secondary)' }}>
                <p><strong style={{ color: 'var(--text-primary)' }}>Email:</strong> {hotelInfo.contact.email}</p>
                <p><strong style={{ color: 'var(--text-primary)' }}>Phone:</strong> {hotelInfo.contact.phone}</p>
                <p><strong style={{ color: 'var(--text-primary)' }}>Address:</strong> {hotelInfo.location.address}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default TermsOfServicePage;
